import { SVG } from "@svgdotjs/svg.js";
import { useToast, UseToastOptions } from "@chakra-ui/react";
import { ReactNode } from "react";

import type { ImageNativeNode } from "@noli/native";
import {
  AABB,
  argMin,
  BBox,
  Coordinate,
  Dictionary,
  getCenteredBBox,
  getSVGBase64DataURL,
  HitTest,
  ImageNode,
  INode,
  INodes,
  INodeType,
  IRectangleShapeNode,
  isString,
  isUndefined,
  Lang,
  Matrix2D,
  Matrix2DFields,
  range,
  RectangleShapeNode,
  runGroupContext,
  setDefault,
  sortBy,
} from "@noli/core";
import { exportPathNode } from "@noli/svg";
import {
  BoardStore,
  BusinessOpCallbacks,
  flagsStore,
  translate,
  useSafeExecute,
} from "@noli/business";

import {
  AIMetaType,
  getOriginOpChain,
  MetaType,
  NoliboxMetaModelType,
  processedMetaType,
} from "@/types/meta";
import { allAIMetaTypes, allMetaTypes, getOriginMeta } from "@/types/meta";
import { Editor_Words } from "./lang/editor";
import { Txt2ImgSizes } from "@/stores/status";
import { isThirdPartyMeta } from "@/thirdParty/meta";
import { THIRD_PARTY_SORT_KEY } from "@/thirdParty/constants";

export function useOwnToast(defaultOptions?: UseToastOptions): ReturnType<typeof useToast> {
  return useToast({
    position: "top",
    isClosable: true,
    duration: 3000,
    ...(defaultOptions ?? {}),
  });
}

export function toast(
  toastFn: ReturnType<typeof useToast>,
  status: UseToastOptions["status"],
  message: ReactNode,
  duration?: number | null,
): void {
  toastFn({
    position: "top",
    title: message,
    status,
    isClosable: true,
    duration:
      typeof duration === "undefined"
        ? status === "error"
          ? 6000
          : status === "warning"
          ? 5000
          : 3000
        : duration,
    containerStyle: {
      width: "400px",
    },
  });
}

export function getImgWH(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  alias: string,
  noToast?: boolean,
): { boardNode: ImageNativeNode; imgWH: { w: number; h: number }; noToast?: boolean } | undefined {
  const boardNode = BoardStore.board.getNode(alias) as ImageNativeNode | undefined;
  if (!boardNode) {
    if (noToast) return;
    toast(t, "warning", translate(Editor_Words["cannot-find-node-warning-message"], lang));
    return;
  }
  if (!boardNode.imgWH) {
    if (noToast) return;
    toast(t, "info", translate(Editor_Words["loading-image-message"], lang));
    return;
  }
  return { boardNode, imgWH: boardNode.imgWH };
}

export function toFixed(n: number, digit: number): number {
  return parseFloat(n.toFixed(digit));
}

export function getNewRectangle(
  alias: string,
  info: BBox | { autoFit: boolean; wh: { w: number; h: number } },
): RectangleShapeNode {
  const { w, h } = info.wh;
  const node = new RectangleShapeNode(
    alias,
    { x: 0, y: 0, w, h },
    BBox.from(0, 0, w, h).fields,
    { z: BoardStore.graph.minZIndex - 1 },
    [{ type: "color", src: "rgb(253, 254, 255)", opacity: 1.0 }],
    [{ width: 1, color: "#333333", opacity: 0.6 }],
  );
  if (info instanceof BBox) {
    node.bboxFields = info.fields;
  } else if (info.autoFit) {
    node.bboxFields = getCenteredBBox(w, h, BoardStore.board).fields;
  }
  return node;
}

export function rectangleToImage(
  rectangle: IRectangleShapeNode,
  src: string,
  alias?: string,
): ImageNode {
  return new ImageNode(
    alias ?? rectangle.alias,
    rectangle.params,
    rectangle.bboxFields,
    rectangle.layerParams,
    undefined,
    undefined,
    { src },
  );
}

export type VirtualInfo = IRectangleShapeNode | { w: number; h: number; type?: undefined };
export type NewImageInfo = VirtualInfo | (BBox & { type?: undefined });

export function getNewImageNode(alias: string, src: string, info: NewImageInfo): ImageNode {
  const newRectangle = info.type
    ? info
    : getNewRectangle(alias, info instanceof BBox ? info : { autoFit: true, wh: info });
  return rectangleToImage(newRectangle, src);
}

export function addNewImage(
  alias: string,
  src: string,
  opt: {
    info: NewImageInfo;
    meta: MetaType;
    callbacks?: BusinessOpCallbacks;
    noSelect?: boolean;
  },
): void {
  const newImage = getNewImageNode(alias, src, opt.info);
  console.log("newImage: ", newImage);

  newImage.meta = opt.meta;
  useSafeExecute("addJson", null, true, opt.callbacks, {
    noSelect: opt.noSelect ?? true,
    safeOpt: {
      retry: 3,
      retryInterval: 500,
    },
  })({ alias, json: newImage.toJson() });
}

export function getPreviewSrc(node: INode | undefined): string | undefined;
export function getPreviewSrc(alias: string | undefined): string | undefined;
export function getPreviewSrc(inp: INode | string | undefined): string | undefined {
  if (isUndefined(inp)) {
    return undefined;
  }
  if (isString(inp)) {
    inp = BoardStore.graph.getNode(inp);
    if (isUndefined(inp)) {
      return undefined;
    }
  }
  flagsStore.latestRecordAliases.has(inp.alias);
  if (inp.type === "image") {
    return inp.renderParams.src;
  }
  if (inp.type === "path") {
    const svg = SVG();
    const pathNode = inp.snapshot();
    pathNode.params.pathTransform = undefined;
    const group = exportPathNode(pathNode);
    svg.node.appendChild(group.node);
    const transform = new Matrix2D(inp.pathTransform).inverse;
    const { x, y, w, h } = new BBox(transform).bounding.toAABB();
    const pad = 1.25 * (inp.useMultipleOptions ? Math.max(...inp.width) : inp.width);
    svg.viewbox(x - pad, y - pad, w + 2 * pad, h + 2 * pad);
    return getSVGBase64DataURL(svg.svg());
  }
  return undefined;
}

export function checkMeta(meta: any): meta is MetaType {
  return allMetaTypes.includes(meta?.type);
}

export function checkIsProcessed(meta: any) {
  if (processedMetaType.includes(meta.type)) {
    return true;
  }
  return processedMetaType.includes(meta.data.from?.type);
}

export function checkAIMeta(meta: any): meta is AIMetaType {
  const type = meta?.type;
  if (isUndefined(type)) return false;
  if (!allAIMetaTypes.includes(type)) {
    return false;
  }
  const data = meta.data;
  const { w, h, text, seed, use_circular } = data;
  if ([w, h, text, seed, use_circular].some((e) => isUndefined(e))) {
    return false;
  }
  return true;
}

const schedules = {
  easeInOutElastic: (x: number): number => {
    const c5 = (2 * Math.PI) / 4.5;
    return x === 0
      ? 0
      : x === 1
      ? 1
      : x < 0.5
      ? -(Math.pow(2, 20 * x - 10) * Math.sin((20 * x - 11.125) * c5)) / 2
      : (Math.pow(2, -20 * x + 10) * Math.sin((20 * x - 11.125) * c5)) / 2 + 1;
  },
  easeOutQuad: (x: number): number => {
    return 1 - (1 - x) * (1 - x);
  },
};
type ScheduleType = keyof typeof schedules;

type IAttachTo = { node: INode; delta: Coordinate } | undefined;
type ArrangePack = {
  node: INode;
  resized: AABB;
  origin: MetaType | undefined;
  attachTo: IAttachTo;
};
type AutoArrangeOptions = {
  padding: number;
  numFrame: number;
  trace: boolean;
  schedule: ScheduleType;
  fitContainer: boolean;
};
function getDefaultArrangeOptions(opt?: Partial<AutoArrangeOptions>): AutoArrangeOptions {
  opt ??= {};
  const padding = opt.padding ?? 32;
  const numFrame = opt.numFrame ?? 90;
  const trace = opt.trace ?? true;
  const schedule = opt.schedule ?? "easeInOutElastic";
  const fitContainer = opt.fitContainer ?? false;
  return { padding, numFrame, trace, schedule, fitContainer };
}
function resizeAutoArrangeTargets(
  targets: INode[],
  commonW: number,
): { scale: number; resized: AABB }[] {
  return targets.map((node) =>
    runGroupContext(
      ({ group }) => {
        const bbox = group.bbox;
        const scale = commonW / bbox.w;
        const resized = bbox.scale(scale);
        group.bbox = resized;
        return { scale, resized: resized.toAABB() };
      },
      new INodes([node]),
      { forceGroup: true },
    ),
  );
}
function arrangeWith(packs: ArrangePack[], commonW: number, padding: number): void {
  // construct gallery
  const numColumns = Math.ceil(Math.sqrt(packs.length));
  const heights: number[] = range(0, numColumns);
  const columns: ArrangePack[][] = range(0, numColumns).map(() => []);
  packs.forEach(({ node, resized, origin, attachTo }) => {
    // 如果当前 pack 需要“附着”在某个节点上，就不进入瀑布流的计算
    if (attachTo) return;
    const targetIdx = argMin(heights);
    columns[targetIdx].push({ node, resized, origin, attachTo });
    if (heights[targetIdx] === 0) {
      heights[targetIdx] = resized.h;
    } else {
      heights[targetIdx] += resized.h + padding;
    }
  });
  // permute with gallery
  columns.forEach((column, i) => {
    let cursor = 0;
    column.forEach(({ node, resized }) => {
      const x = i * (commonW + padding);
      const y = cursor;
      node.bbox = node.bbox.move(new Coordinate(x, y).subtract(resized.position));
      cursor += resized.h + padding;
    });
  });
  // permute with `attachTo`
  packs.forEach(({ node, attachTo }) => {
    if (!attachTo) return;
    const centerTarget = attachTo.node.bbox.center.add(attachTo.delta);
    node.bbox = node.bbox.move(centerTarget.subtract(node.bbox.center));
  });
}
async function animateArrangement(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  original: INode[],
  targets: INode[],
  numFrame: number,
  trace: boolean,
  schedule: ScheduleType,
): Promise<void> {
  if (original.every((node, i) => node.bbox.closeTo(targets[i].bbox, { atol: 0.1, rtol: 0.1 }))) {
    toast(t, "info", translate(Editor_Words["auto-arrange-no-need-message"], lang));
    return;
  }

  async function matchBBox(trace: boolean, fields: Dictionary<Matrix2DFields>): Promise<void> {
    const existingNodes = BoardStore.board.getNodes();
    await BoardStore.board.executer.executeNoBusiness(
      "matchBBox",
      {
        aliases: original
          .filter((node) =>
            node.type === "group"
              ? node.allChildrenNodes.every((n) => existingNodes.has(n.alias))
              : existingNodes.has(node.alias),
          )
          .map((node) => node.alias),
        params: fields,
      },
      trace,
    );
  }

  const scheduleFn = schedules[schedule];
  for (const i of range(0, numFrame)) {
    const fields: Dictionary<Matrix2DFields> = {};
    targets.forEach((node, j) => {
      const r = i / (numFrame - 1);
      fields[node.alias] = original[j].bbox.lerp(node.bbox, scheduleFn(r)).fields;
    });
    await matchBBox(trace && i == numFrame - 1, fields);
  }
}
export async function autoArrange(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  nodes: INode[],
  opt?: Partial<AutoArrangeOptions>,
): Promise<void> {
  if (nodes.length === 0) {
    toast(t, "warning", translate(Editor_Words["auto-arrange-no-nodes-message"], lang));
    return;
  }

  const defaultPrefix = "__";

  const { padding, numFrame, trace, schedule, fitContainer } = getDefaultArrangeOptions(opt);
  const original = nodes.map((node) => node.snapshot());
  const targets = original.map((node) => node.snapshot());
  // group & sort
  const packsGroupByKey: Dictionary<ArrangePack[]> = {};
  const groupLatestTimestamps: Dictionary<number> = {};
  const commonW = Math.min(2048, Math.max(...targets.map((node) => node.w)));
  const checkAttachment = (node: INode) => {
    if (node.type !== "path") return undefined;
    const bbox = node.bbox;
    const overlapped = targets.filter(
      (node) => node.type !== "path" && HitTest.test(bbox, node.bbox),
    );
    if (overlapped.length === 0) return undefined;
    if (overlapped.length === 1) return overlapped[0];
    return sortBy(
      overlapped,
      overlapped.map((node) => node.zIndex),
    )[0];
  };
  const checkAttachmentResponses: Dictionary<INode | undefined> = {};
  targets.forEach((node) => (checkAttachmentResponses[node.alias] = checkAttachment(node)));
  const bases = targets.filter((node) => !checkAttachmentResponses[node.alias]);
  const attachToMapping: Dictionary<IAttachTo> = {};
  targets.forEach((node) => {
    const res = checkAttachmentResponses[node.alias];
    attachToMapping[node.alias] = !res
      ? undefined
      : {
          node: res,
          delta: node.bbox.center.subtract(res.bbox.center),
        };
  });
  const resizedAABBs: Dictionary<AABB> = {};
  const basesResizedRes = resizeAutoArrangeTargets(bases, commonW);
  basesResizedRes.forEach((res, i) => (resizedAABBs[bases[i].alias] = res.resized));
  const scales: Dictionary<number> = {};
  bases.forEach((node, i) => (scales[node.alias] = basesResizedRes[i].scale));
  targets.forEach((node) => {
    const attachTo = attachToMapping[node.alias];
    if (!attachTo) return;
    resizeAutoArrangeTargets([node], node.w * scales[attachTo.node.alias]);
  });
  targets.forEach((node) => {
    const meta = node.type === "group" ? undefined : node.meta;
    let key, origin;
    if (!checkMeta(meta)) {
      key = `${defaultPrefix}${node.alias.split(".").slice(0, -1).join(".")}`;
      origin = undefined;
    } else {
      origin = getOriginMeta(meta);
      key = !origin
        ? defaultPrefix
        : isThirdPartyMeta(origin)
        ? (origin.data as any)[THIRD_PARTY_SORT_KEY[origin.type]]
        : checkAIMeta(origin)
        ? `${origin.data.text} ${origin.data.seed}`
        : origin.data.url;
    }
    const packs = setDefault(packsGroupByKey, key, []);
    packs.push({
      node,
      resized: resizedAABBs[node.alias],
      origin,
      attachTo: attachToMapping[node.alias],
    });
    groupLatestTimestamps[key] = Math.max(
      meta?.data?.timestamp ?? 0,
      groupLatestTimestamps[key] ?? 0,
    );
  });

  const allSortedPacks: ArrangePack[] = [];
  const allKeys = Object.keys(packsGroupByKey);
  const sortedKeys = sortBy(
    allKeys,
    allKeys.map((key) => (key.startsWith(defaultPrefix) ? 0 : groupLatestTimestamps[key])),
  );
  sortedKeys.forEach((key) => {
    const packs = packsGroupByKey[key];
    const sortedPacks = sortBy(
      packs,
      packs.map(({ origin }) => origin?.data.timestamp ?? 0),
    );
    sortedPacks.forEach((pack) => allSortedPacks.push(pack));
  });
  // arrange to grid
  arrangeWith(allSortedPacks, commonW, padding);
  // align to center
  runGroupContext(
    ({ group }) => {
      const aabb = group.bbox.bounding.toAABB();
      const { w, h } = aabb;
      let bounding;
      if (fitContainer) {
        bounding = BoardStore.board.screen;
      } else {
        const center = runGroupContext(({ group }) => group.bbox.center, new INodes(original));
        const { x, y } = center.subtract(aabb.center);
        bounding = new AABB(x, y, w, h);
      }
      const centered = getCenteredBBox(w, h, bounding);
      group.bbox = centered;
    },
    new INodes(targets),
    { forceGroup: true },
  );
  animateArrangement(t, lang, original, targets, numFrame, trace, schedule);
}

export function onArrange(
  t: ReturnType<typeof useToast>,
  lang: Lang,
  { type, nodes }: { type: "none" | "multiple"; nodes: INodeType[] },
): void {
  autoArrange(
    t,
    lang,
    type === "none" ? BoardStore.graph.rootNodes.filter((node) => !node.noSave) : nodes,
    { fitContainer: type === "none" },
  );
}

type ParsedLabels = {
  // 是否是「AI 生成的图片」
  isAI: boolean;
  // 是否是「文字生成图片」
  isTxt2Img: boolean;
  // 是否是「图片转换」
  isImg2Img: boolean;
  // 是否是「循环图样生成」
  isCircular: boolean;
  // 是否是「动漫图片生成」
  isAnime: boolean;
  // 是否是「超分辨率」
  isSuperResolution: boolean;
  // 是否是「图像修补 inpainting」
  isInpainting: boolean;
  // 是否是「合成风景」
  isLandscapeSynthesis: boolean;
  // 是否是「动漫超分辨率」
  isAnimeSuperResolution: boolean;
  // 是否是「文心生成」
  isWenxin: boolean;
  // 是否是「图像外延」
  isOutpainting: boolean;
  // 用的 sd 版本
  version?: string;
  // 是否是局部替换
  isScopeReplace: boolean;
  // 模型
  model?: NoliboxMetaModelType;
};
export function parseLabels(meta: any): ParsedLabels {
  const labels: ParsedLabels = {
    isAI: false,
    isTxt2Img: false,
    isImg2Img: false,
    isCircular: false,
    isAnime: false,
    isLandscapeSynthesis: false,
    isSuperResolution: false,
    isAnimeSuperResolution: false,
    isInpainting: false,
    isWenxin: false,
    isOutpainting: false,
    isScopeReplace: false,
  };
  if (!checkMeta(meta)) return labels;
  const opChain = getOriginOpChain(meta);
  const isAI = checkAIMeta(opChain[opChain.length - 1]) || checkAIMeta(opChain[0]);
  labels.isAI = isAI;
  if (opChain[opChain.length - 1].type === "wenxin") {
    labels.isWenxin = true;
  }
  for (let i = 0; i < opChain.length; i++) {
    const origin = opChain[i];
    switch (origin.type) {
      case "txt2img":
      case "txt2img.variation":
        labels.isTxt2Img = true;
        labels.version = origin.data.version;
        break;
      case "txt2img.outpainting":
        labels.isOutpainting = true;
        break;
      case "txt2img.inpainting":
        labels.isScopeReplace = true;
        break;
      case "img2img":
      case "img2img.variation":
        labels.isImg2Img = true;
        labels.version = origin.data.version;
        break;
      case "inpainting":
        labels.isInpainting = true;
        break;
      case "landscape":
        labels.isLandscapeSynthesis = true;
        break;
    }
    if (checkAIMeta(origin)) {
      labels.model = "basic";
      if (origin.data.use_circular) {
        labels.isCircular = true;
      }
      if (origin.data.is_anime) {
        labels.isAnime = true;
        labels.model = "anime";
      }

      if (origin.data.model) {
        labels.model = origin.data.model;
      }
    }
  }
  if (meta.type === "sr") {
    meta.data.is_anime ? (labels.isAnimeSuperResolution = true) : (labels.isSuperResolution = true);
  }
  return labels;
}

export const getTxt2imgGenerateSize = (txt2imgSize: Txt2ImgSizes): { w: number; h: number } => {
  switch (txt2imgSize) {
    case "3:4":
      return { w: 512, h: 704 };
    case "4:3":
      return { w: 704, h: 512 };
    case "1:1":
    default:
      return { w: 512, h: 512 };
  }
};

export function timestampToDate(timestamp: number): string {
  const date = new Date(timestamp);
  const month = date.getMonth() + 1;
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const seconds = ("0" + date.getSeconds()).slice(-2);
  return `${date.getFullYear()}.${month}.${date.getDate()} ${date.getHours()}:${minutes}:${seconds}`;
}
